import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from 'react-intl';

import {
    Modal,
    Form,
    Select,
    Button,
    Tooltip,
    IconButton
} from '@gpstrackit/gtc-react-controls';

import moment from 'moment';

import TableView from 'Components/TableView';
import { NotificationManager, NotificationContainer } from 'Components/Notifications';
import { Timezone } from 'Components/uiControls';
import { speedFormatMPH, getFormattedDateByTimeZone, postedSpeedColor } from 'Core/data/Helpers';
import { getVidFleetEvent, getSwitnessEvent } from '../../../../core/data/VideoEvents';
import { Grid, Typography, Icon } from '@mui/material';
import { getEventColor } from '../../../../components/Map/utils';
import { Avatar } from 'Components';

import { translateEvent } from '../../../.common/utils/translateEvents';

//actions
import {
    reset,
    closeVideoAssignDriverModal,
    getTripInfoFromEvent,
    searchVehicleTrailsForAssignDriver,
    assignDriverToEvents
} from './redux/slice';

import {
    catalogLoad,
} from 'Redux/actions';

import './styles.scss';

const VideoAssignDriver = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const { refresh, isDriverBehavior } = props;

    const assignDriverToEventsFormRef = Form.useForm();

    const lan = useSelector(state => state.commomRedux.language);

    const driversWithAvatar = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const loadingDrivers = useSelector(state => state.catalogsRedux.loadingDrivers);
    const { user } = useSelector(state => state.securityRedux);

    const { dateformat, isKph } = user;

    const open = useSelector(state => state.videoAssignDriverRedux.open);
    const response = useSelector(state => state.videoAssignDriverRedux.response);
    const loading = useSelector(state => state.videoAssignDriverRedux.loading);
    const event = useSelector(state => state.videoAssignDriverRedux.event);
    const trip = useSelector(state => state.videoAssignDriverRedux.trip);
    const trails = useSelector(state => state.videoAssignDriverRedux.trails);
    const filters = useSelector(state => state.videoAssignDriverRedux.filters);
    const total = useSelector(state => state.videoAssignDriverRedux.total);
    const error = useSelector(state => state.videoAssignDriverRedux.error);

    const [page, setPage] = useState(0);
    const [searchText, setSearchText] = useState("");


    useEffect(() => {
        if (open && event) {
            dispatch(getTripInfoFromEvent(event));
        }
    }, [open, event]);

    useEffect(() => {
        if (open && response) {
            NotificationManager.success(
                messages['videoAssignDriver_successMessage'],
                messages['videoAssignDriver_success'],
                3000,
                null,
                null,
                'success',
                null
            );

            if (refresh) refresh();
            handleClose();
        }
    }, [open, response]);

    useEffect(() => {
        if (open && error) {
            let errorMessage = messages["errorMessage"];
            if (error?.error == "Error while getting trips, ignition on not found") {
                errorMessage = messages["videoAssignDriver_errorStartNotFound"];
            } else if (error?.error == "Error while assigning driver, the driver is already assigned to other trip") {
                errorMessage = messages['videoAssignDriver_errorDriverNotAvailable']
            }

            NotificationManager.error(
                errorMessage,
                messages["error"],
                5000,
                null,
                null,
                "warning"
            );

            dispatch(reset({ error: null, response: null }));
        }
    }, [error, open]);

    const searchVehicleTrails = (newSearch, newOffset, reset = true) => {
        dispatch(searchVehicleTrailsForAssignDriver({
            deviceId: event?.deviceId,
            openSearch: newSearch,
            startDate: trip?.tripStart?.unitTime,
            endDate: trip?.tripEnd?.unitTime || moment().utc().toISOString(),
            offset: newOffset,
            limit: filters?.limit,
            kph: isKph,
            reset: reset
        }));
    }

    const handleClose = useCallback(() => {
        dispatch(closeVideoAssignDriverModal());
        dispatch(reset());
    });

    const onChangePage = useCallback((newPage) => {
        if (loading) return //prevents multiple requests

        const newOffset = (newPage - 1) * filters?.limit;
        searchVehicleTrails(searchText, newOffset, false);
        setPage(newPage);
    });

    const renderCurrentDriver = useCallback((_trip) => {
        if (!_trip || !driversWithAvatar?.length) {
            return;
        }

        if (!_trip?.driverId) {
            return (
                <span>{messages["videoClipRequest_unassigned"]}</span>
            )
        }

        let _driver = driversWithAvatar?.find((d) => d?.driverId == _trip?.driverId);
        let driverName = trip?.tripStart?.driverName ? `${trip?.tripStart?.driverName} ${_driver?.externalId ? `(${_driver?.externalId})` : ""}` : messages["videoClipRequest_unassigned"];

        return (
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                alignContent="center"
                alignSelf="center"
                wrap="nowrap"
            >
                <Grid item>
                    <Avatar fileKey={_driver?.avatarFile?.fileKey} name={trip?.tripStart?.driverName} />
                </Grid>

                <Grid item xs zeroMinWidth>
                    <Tooltip title={driverName}>
                        <Typography noWrap style={{ fontSize: 14, paddingLeft: 8 }}>
                            {driverName}
                        </Typography>
                    </Tooltip>
                </Grid>
            </Grid>
        )
    });

    return (
        <Modal
            title={`${messages['videoAssignDriver_title']}`}
            open={open}
            loading={loading || loadingDrivers}
            position="right"
            size="small"
            handleClose={handleClose}
            footer={(
                <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                    <Button
                        disabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant='secondary'
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loading}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            assignDriverToEventsFormRef?.submit();
                        }}
                        variant='primary'
                    >
                        {messages['save']}
                    </Button>
                </div>
            )}
        >
            <div className='assign-driver-form-container'>
                <Form
                    ref={assignDriverToEventsFormRef.ref}
                    loading={loading}
                    onValidSubmit={(values) => {
                        dispatch(assignDriverToEvents({
                            id: event?.id || 0,
                            unitId: event?.unitId || 0,
                            driverName: values?.driver?.name || "",
                            driverId: values?.driver?.driverId || 0,
                            tripStartTime: trip?.tripStart?.unitTime,
                            tripEndTime: trip?.tripEnd?.unitTime,
                            oldDriverId: event?.driverId || 0
                        }));
                    }}
                >
                    {
                        isDriverBehavior &&
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            style={{ marginBottom: 16 }}
                        >
                            <Grid item>
                                <IconButton
                                    icon="info"
                                    style={{ marginRight: 8 }}
                                />
                            </Grid>
                            <Grid item xs>
                                <span style={{ fontSize: 14 }}>
                                    {messages['videoAssignDriver_warningDriverBehavior']}
                                </span>
                            </Grid>
                        </Grid>
                    }
                    <Form.Item
                        name="driver"
                        label={messages['videoAssignDriver_driver']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Select
                            id="driver"
                            options={(driversWithAvatar?.filter((d) => d?.driverId != event?.driverId) || []).map((d) => {
                                return { ...d, name: `${d.name}${d?.externalId ? ` (${d?.externalId})` : ""}` }
                            }).sort((a, b) => a?.name?.localeCompare(b?.name?.trim()))}
                            placeholder={messages["videoAssignDriver_driver"]}
                            valueField="driverId"
                            textField="name"
                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                            isDisabled={!trip?.tripStart}
                            isSearchable
                        />
                    </Form.Item>
                </Form>

                {
                    trip &&
                    <div>
                        {
                            event &&
                            <div className='assign-driver-row assign-driver-card'>
                                <div className='assign-driver-trip-title-container'>
                                    <span className='assign-driver-trip-title'>{messages["videoAssignDriver_currentDriver"]}</span>
                                </div>

                                <div style={{ width: 270 }}>
                                    {renderCurrentDriver(event)}
                                </div>
                            </div>
                        }

                        {
                            trip?.tripStart &&
                            <div className='assign-driver-row assign-driver-card'>
                                <div className='assign-driver-trip-title-container'>
                                    <span className='assign-driver-trip-title'>{messages["videoAssignDriver_start"]}</span>
                                </div>
                                <div>
                                    <div>
                                        <span className='assign-driver-trip-start-event'>{messages["videoAssignDriver_ignitionOn"]}</span>
                                    </div>
                                    <div>
                                        <Timezone
                                            date={trip?.tripStart?.unitTime}
                                            dateFormat={dateformat}
                                            timeFormat="hh:mm a"
                                            timezone={trip?.unit?.timeZone?.name}
                                            className="gtc-video-date-time"
                                        />
                                    </div>
                                </div>
                            </div>
                        }


                        <div className='assign-driver-row assign-driver-card'>
                            <div className='assign-driver-trip-title-container'>
                                <span className='assign-driver-trip-title'>{messages["videoAssignDriver_end"]}</span>
                            </div>

                            {
                                trip?.tripEnd ?
                                    <div>
                                        <div>
                                            <span className='assign-driver-trip-end-event'>{messages["videoAssignDriver_ignitionOff"]}</span>
                                        </div>
                                        <div>
                                            <Timezone
                                                date={trip?.tripEnd?.unitTime}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={trip?.unit?.timeZone?.name}
                                                className="gtc-video-date-time"
                                            />
                                        </div>
                                    </div> :
                                    <div className='assign-driver-trip-end-event'>{messages['videoAssignDriver_tripNotFinished']}</div>
                            }

                        </div>

                    </div>
                }

                <div>
                    {
                        trip?.tripStart &&
                        <TableView
                            data={trails || []}
                            loading={loading || loadingDrivers}
                            page={page}
                            rowsPerPage={25}
                            name="events-list"
                            topSpaceSize={242}
                            infiniteScroll={true}
                            viewType="list"
                            messages={messages}
                            itemSelector={false}
                            serverSide
                            onChangePage={onChangePage}
                            total={trails?.total || 0}
                            moreRowsAvailable={(trails?.length || 0) < (total || 0)}
                            renderRow={(row) => {
                                let event;

                                if (row?.eventCode && row?.eventCode?.includes("CameraEvent")) {
                                    event = getVidFleetEvent(row?.eventCode);
                                }

                                if (row?.unitType === "switness") {
                                    event = getSwitnessEvent(element?.eventCode);
                                }
                                let translateEventValue =  translateEvent(row?.eventName, messages)

                                return (
                                    <div className='assign-driver-card' style={{ marginRight: 1, marginLeft: 1 }}>
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            alignContent="center"
                                            alignSelf="center"
                                            wrap="nowrap"
                                        >
                                            <Icon style={{ color: event?.color || getEventColor(row.eventName) }} className='icon-card'>
                                                play_circle
                                            </Icon>

                                            <Grid item xs zeroMinWidth>
                                                <Typography
                                                    noWrap
                                                    variant="subtitle1"
                                                    className="vehicle-trail-list-title"
                                                    style={{ fontWeight: 'bold' }}
                                                >
                                                    {translateEventValue ? translateEventValue : event && event?.lanKey ? messages[event?.lanKey] : (row?.eventName || 'N/A')}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            alignContent="center"
                                            alignSelf="center"
                                            wrap="nowrap"
                                        >
                                            <Icon style={{ color: 'gray' }} className='icon-card'>
                                                query_builder
                                            </Icon>

                                            <Grid item xs zeroMinWidth>
                                                <Typography noWrap variant="subtitle2">
                                                    <Timezone
                                                        date={new Date(row?.unitTime)}
                                                        dateFormat={""}
                                                        timeFormat="hh:mm:ss a"
                                                        timezone={trip?.unit?.timeZone?.name}
                                                        className="labelTrailDate"
                                                        customFormat={true}
                                                        locale={lan}
                                                        noWrap={false}
                                                        oneControl={true}
                                                    />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                                                    <Icon className='icon-card'>
                                                        insert_invitation
                                                    </Icon>

                                                    <Typography className="vehicle-trail-list-subtitle" noWrap variant="subtitle2">
                                                        {getFormattedDateByTimeZone(row?.unitTime, trip?.unit?.timeZone?.name, messages, "MM/DD/YYYY", false, lan)}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            alignContent="center"
                                            alignSelf="center"
                                            wrap="nowrap"
                                        >
                                            <Icon className='icon-card'>
                                                room
                                            </Icon>

                                            <Grid item xs zeroMinWidth>
                                                <Tooltip title={row?.formattedAddress}>
                                                    <Typography noWrap variant="subtitle2">
                                                        {row?.formattedAddress}
                                                    </Typography>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            alignContent="center"
                                            alignSelf="center"
                                            wrap="nowrap"
                                        >
                                            <Icon className='icon-card'>
                                                speed
                                            </Icon>

                                            <Grid item>
                                                <span
                                                    style={{ fontSize: 16, color: postedSpeedColor(row.speed, row.speedLimit) }}
                                                >
                                                    {`${row?.speed ? speedFormatMPH(row?.speed, row?.unitType, isKph, true, true) : 0}`}
                                                </span>
                                            </Grid>

                                            <Grid item>
                                                {
                                                    row?.speedLimit
                                                    &&
                                                    <div
                                                        style={{
                                                            textTransform: 'lowercase',
                                                            fontSize: 14,
                                                            padding: '3px 0px 2px 0px', // Añade un espacio alrededor del texto para que el borde no quede justo alrededor del número
                                                            border: '1px solid black', // Crea un borde sólido de 2px en color negro
                                                            width: 16,
                                                            height: 12,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginLeft: 4
                                                        }}
                                                    >
                                                        {`${row.speedLimit ? speedFormatMPH(row?.speedLimit, row?.unitType, isKph, true, true) : '?'}`}
                                                    </div>
                                                }
                                            </Grid>

                                            <Grid item>
                                                <span
                                                    style={{ textTransform: 'lowercase', paddingLeft: 4, color: "black" }}
                                                >
                                                    {`${isKph ? 'kph' : 'mph'}`}
                                                </span>
                                            </Grid>
                                        </Grid>

                                        {
                                            row?.landmark?.name &&
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="center"
                                                alignContent="center"
                                                alignSelf="center"
                                                wrap="nowrap"
                                            >
                                                <Icon
                                                    className='icon-card'
                                                //TODO, maybe we can display the landmark icon and color
                                                //style={{ color: row?.landmark?.color ? `#${row?.landmark?.color}` : "black" }}
                                                >
                                                    flag
                                                </Icon>

                                                <Grid item xs zeroMinWidth>
                                                    <Tooltip title={row?.landmark?.name}>
                                                        <Typography noWrap variant="subtitle2">
                                                            {row?.landmark?.name}
                                                        </Typography>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        }
                                    </div>
                                )
                            }}
                        />
                    }
                </div>
            </div>

            <NotificationContainer />
        </Modal>
    );
}

export default VideoAssignDriver;